import { useCallback, useEffect, useMemo, useState } from 'react';

import { ExtendedLiveReportList } from '@futrli/views/processors/dashboards/dashboardsList';

import { readPersistedFilters, useListFilterPersistence } from '../../utils/list-filters';

const STORAGE_KEY = 'futrli:my-dashboards-filters';

interface DashboardFilters {
  statusFilterValue: string;
  sortByFilterValue: string;
}

const matchesSearch = (dashboard: ExtendedLiveReportList, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true;
  }
  return dashboard.name.toLowerCase().includes(searchTerm.toLowerCase());
};

const matchesStatus = (
  dashboard: ExtendedLiveReportList,
  statusFilterValue: string,
  isResourceSharingEnabled: boolean
): boolean => {
  if (statusFilterValue === 'all') {
    return true;
  }
  if (statusFilterValue === 'performanceDashboard') {
    return dashboard.isPerformanceDashboard;
  }
  if (isResourceSharingEnabled) {
    return dashboard.permission_info?.sharing_status === statusFilterValue;
  }
  return dashboard.status === statusFilterValue && !dashboard.isPerformanceDashboard;
};

const getValidStatusFilter = (statusFilterValue: string, isResourceSharingEnabled: boolean): string => {
  const validValues = isResourceSharingEnabled
    ? ['all', 'shared', 'private', 'performanceDashboard']
    : ['all', 'live', 'draft', 'performanceDashboard'];

  return validValues.includes(statusFilterValue) ? statusFilterValue : 'all';
};

const sortDashboards = (dashboards: ExtendedLiveReportList[], sortByFilterValue: string): ExtendedLiveReportList[] => {
  const sorted = [...dashboards];

  switch (sortByFilterValue) {
    case 'date-created-newest':
      return sorted.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    case 'date-created-oldest':
      return sorted.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
    case 'name-a-z':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'name-z-a':
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
    default:
      return sorted;
  }
};

export const useMyDashboardsFilters = (
  dashboards: ExtendedLiveReportList[],
  organisationId: string,
  isResourceSharingEnabled = false
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilterValue, setStatusFilterValue] = useState(() => {
    const persisted = readPersistedFilters(STORAGE_KEY, organisationId);
    return getValidStatusFilter((persisted?.filters?.statusFilterValue as string) ?? 'all', isResourceSharingEnabled);
  });
  const [sortByFilterValue, setSortByFilterValue] = useState(() => {
    const persisted = readPersistedFilters(STORAGE_KEY, organisationId);
    return (persisted?.filters?.sortByFilterValue as string) ?? 'date-created-newest';
  });
  useEffect(() => {
    setStatusFilterValue(current => getValidStatusFilter(current, isResourceSharingEnabled));
  }, [isResourceSharingEnabled]);

  const onSearchChange = useCallback((value: string) => setSearchTerm(value), []);
  const onStatusFilterChange = useCallback((value: string) => setStatusFilterValue(value), []);
  const onSortByFilterChange = useCallback((value: string) => setSortByFilterValue(value), []);

  const filters = useMemo<DashboardFilters>(() => ({ statusFilterValue, sortByFilterValue }), [
    statusFilterValue,
    sortByFilterValue
  ]);

  const handleRestore = useCallback(
    (restoredFilters: DashboardFilters) => {
      setStatusFilterValue(getValidStatusFilter(restoredFilters.statusFilterValue, isResourceSharingEnabled));
      setSortByFilterValue(restoredFilters.sortByFilterValue);
    },
    [isResourceSharingEnabled]
  );

  useListFilterPersistence({
    storageKey: STORAGE_KEY,
    organisationId,
    filters,
    sort: undefined,
    onRestore: handleRestore
  });

  const filteredDashboards = useMemo(() => {
    const filtered = dashboards.filter(
      dashboard =>
        matchesSearch(dashboard, searchTerm) && matchesStatus(dashboard, statusFilterValue, isResourceSharingEnabled)
    );

    return sortDashboards(filtered, sortByFilterValue);
  }, [dashboards, searchTerm, statusFilterValue, sortByFilterValue, isResourceSharingEnabled]);

  return {
    searchTerm,
    onSearchChange,
    statusFilterValue,
    onStatusFilterChange,
    sortByFilterValue,
    onSortByFilterChange,
    filteredDashboards
  };
};
